import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AuthUser, LoginResponse } from '../../models/auth/auth.models';

@Injectable({ providedIn: 'root' })
export class AuthService  {
  private userSubject = new BehaviorSubject<AuthUser | null>(this.restore());
  readonly user$ = this.userSubject.asObservable();
  readonly isAuthenticated$ = this.user$.pipe(map(u => !!u));

  constructor(private http: HttpClient) {  }

  async login(email: string, password: string): Promise<void> {
    const res = await firstValueFrom(this.http.post<LoginResponse>(`${environment.apiBaseUrl}/auth/login/web`, { email, password }));
    const user: AuthUser = {
      ...res.user,
      avatarUrl: (res.user as any).profilePicture ?? res.user.avatarUrl,
    };
    localStorage.setItem('authToken', res.token);
    localStorage.setItem('authUser', JSON.stringify(user));
    this.userSubject.next(user);
  }

  logout(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    this.userSubject.next(null);
  }

  get currentUser(): AuthUser | null {
    return this.userSubject.value;
  }

  getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  async forgotPassword(email: string): Promise<void> {
    await firstValueFrom(
      this.http.post<void>(`${environment.apiBaseUrl}/no-auth/forgot`, { email })
    );
  }

  async resetPassword(code: string, password: string, confirmPassword: string): Promise<void> {
    await firstValueFrom(
      this.http.post<void>(`${environment.apiBaseUrl}/no-auth/reset`, { code, password, confirmPassword })
    );
  }

  patchUser(partial: Partial<AuthUser>): void {
    const current = this.userSubject.value;
    if (!current) return;
    const updated = { ...current, ...partial };
    localStorage.setItem('authUser', JSON.stringify(updated));
    this.userSubject.next(updated);
  }

  private restore(): AuthUser | null {
    const raw = localStorage.getItem('authUser');
    return raw ? JSON.parse(raw) as AuthUser : null;
  }
}
